import React from 'react';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  TextField,
} from '@mui/material';
import {
  DatePicker,
  DatePickerValue,
} from '../../../../components/date-picker/date-picker';
import { UserLookup } from '../../../../components/lookups/user-lookup/user-lookup';
import {
  TimePicker,
  TimePickerValue,
} from '../../../../components/time-picker';
import type { ValidationErrors } from './dialogs/use-service-provider-event-form-state';
import { SingleSelect } from '../../../../components/single-select/single-select';
import { JournalNoteContentTypeModel, JournalNoteContentTypeNoteType2 } from '../../../../api-service/clients';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { useSelectedAuthorityOptic } from '../../authority/authority-optic/authority-optic-context';
import { useTenantAddons, TenantAddonConstants } from '../../../../components/tenant-addons/tenant-addons-provider';

interface ServiceProviderEventFormFieldsProps {
  title: string;
  locationAddress: string;
  responsibleUserId?: string;
  allDay?: boolean;
  startDate: DatePickerValue;
  startTime: TimePickerValue;
  endDate: DatePickerValue;
  endTime: TimePickerValue;
  content: string;
  noteTypeId?: string;
}

interface ServiceProviderEventFormFieldsEditProps
  extends ServiceProviderEventFormFieldsProps {
  readOnly?: false;
  errors?: ValidationErrors;
  onTitleChange: (value: string) => void;
  onLocationAddressChange: (value: string) => void;
  onResponsibleUserIdChange: (value: string | undefined) => void;
  onAllDayChange?: (value: boolean) => void;
  onStartDateChange: (value: DatePickerValue) => void;
  onStartDateValidation: (error: string | undefined) => void;
  onStartTimeChange: (value: TimePickerValue) => void;
  onStartTimeValidation: (error: string | undefined) => void;
  onEndDateChange: (value: DatePickerValue) => void;
  onEndDateValidation: (error: string | undefined) => void;
  onEndTimeChange: (value: TimePickerValue) => void;
  onEndTimeValidation: (error: string | undefined) => void;
  onContentChange: (value: string) => void;
  onNoteTypeIdChange: (value: string | undefined) => void;
}

interface ServiceProviderEventFormFieldsReadOnlyProps
  extends ServiceProviderEventFormFieldsProps {
  readOnly: true;
  errors?: never;
  onTitleChange?: never;
  onLocationAddressChange?: never;
  onResponsibleUserIdChange?: never;
  onAllDayChange?: never;
  onStartDateChange?: never;
  onStartDateValidation?: never;
  onStartTimeChange?: never;
  onStartTimeValidation?: never;
  onEndDateChange?: never;
  onEndDateValidation?: never;
  onEndTimeChange?: never;
  onEndTimeValidation?: never;
  onContentChange?: never;
  onNoteTypeIdChange?: never;
}

type ServiceProviderEventFormFieldsAllProps =
  | ServiceProviderEventFormFieldsEditProps
  | ServiceProviderEventFormFieldsReadOnlyProps;

const journalClient = moduleClientFactory.createCitizenJournalClient();

export function ServiceProviderEventFormFields(
  props: ServiceProviderEventFormFieldsAllProps
) {
  const {
    readOnly = false,
    errors,
    title,
    onTitleChange = () => undefined,
    locationAddress,
    onLocationAddressChange = () => undefined,
    responsibleUserId,
    onResponsibleUserIdChange = () => undefined,
    allDay = false,
    onAllDayChange = () => undefined,
    startDate,
    onStartDateChange = () => undefined,
    onStartDateValidation = () => undefined,
    startTime,
    onStartTimeChange = () => undefined,
    onStartTimeValidation = () => undefined,
    endDate,
    onEndDateChange = () => undefined,
    onEndDateValidation = () => undefined,
    endTime,
    onEndTimeChange = () => undefined,
    onEndTimeValidation = () => undefined,
    content,
    onContentChange = () => undefined,
    noteTypeId,
    onNoteTypeIdChange = () => undefined,
  } = props;

  const opticId = useSelectedAuthorityOptic();
  const { isTenantAddonEnabled } = useTenantAddons();
  const hasSocialUdfoerer = isTenantAddonEnabled([TenantAddonConstants.SocialUdfoerer]);

  const [contentTypeOptions, setContentTypeOptions] = React.useState<JournalNoteContentTypeModel[]>([]);

  React.useEffect(() => {
    journalClient.getContentTypes().then(response => {
      let types = (response.contentTypes ?? []).filter(ct => ct.isActive);

      if (hasSocialUdfoerer) {
        types = types.filter(
          ct => !ct.noteTypes?.length || ct.noteTypes.includes(JournalNoteContentTypeNoteType2.Diary)
        );
      }

      if (opticId) {
        types = types.filter(
          ct => !ct.applicableToJournalNoteOptics?.length || ct.applicableToJournalNoteOptics.includes(opticId)
        );
      }

      setContentTypeOptions(types);
    });
    // Load once on mount; optic and social-udfoerer are session-stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedContentType = contentTypeOptions.find(ct => ct.id === noteTypeId) ?? null;

  return (
    <>
      <TextField
        label="Titel"
        value={title}
        onChange={event => onTitleChange(event.target.value)}
        fullWidth
        required={!readOnly}
        error={!!errors?.title}
        helperText={errors?.title}
        sx={{ mt: 2 }}
        disabled={readOnly}
      />
      <TextField
        label="Adresse"
        value={locationAddress}
        onChange={event => onLocationAddressChange(event.target.value)}
        fullWidth
        sx={{ mt: 2 }}
        disabled={readOnly}
      />
      <Box sx={{ mt: 2, width: '100%' }}>
        <UserLookup
          label="Ansvarlig medarbejder"
          value={responsibleUserId}
          onChange={onResponsibleUserIdChange}
          variant="outlined"
          disabled={readOnly}
        />
      </Box>
      <Divider sx={{ mt: 2 }} />
      <FormControlLabel
        sx={{ mt: 1 }}
        control={
          <Checkbox
            checked={allDay}
            onChange={event => onAllDayChange(event.target.checked)}
            disabled={readOnly}
          />
        }
        label="Hele dagen"
      />
      <Box sx={{ display: 'flex', gap: 2, mt: 2, width: '100%' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DatePicker
            label="Start dato"
            value={startDate}
            onChange={onStartDateChange}
            onInternalValidation={onStartDateValidation}
            fullWidth
            required={!readOnly}
            error={!!errors?.startDate}
            errorText={errors?.startDate}
            variant="outlined"
            disabled={readOnly}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TimePicker
            label="Start tid"
            value={startTime}
            onChange={onStartTimeChange}
            onInternalValidation={onStartTimeValidation}
            fullWidth
            required={!readOnly}
            error={!!errors?.startTime}
            errorText={errors?.startTime}
            variant="outlined"
            disabled={readOnly || allDay}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mt: 2, width: '100%' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DatePicker
            label="Slut dato"
            value={endDate}
            onChange={onEndDateChange}
            onInternalValidation={onEndDateValidation}
            fullWidth
            required={!readOnly}
            error={!!errors?.endDate}
            errorText={errors?.endDate}
            variant="outlined"
            disabled={readOnly || allDay}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TimePicker
            label="Slut tid"
            value={endTime}
            onChange={onEndTimeChange}
            onInternalValidation={onEndTimeValidation}
            fullWidth
            required={!readOnly}
            error={!!errors?.endTime}
            errorText={errors?.endTime}
            variant="outlined"
            disabled={readOnly || allDay}
          />
        </Box>
      </Box>
      <Divider sx={{ mt: 2 }} />
      <TextField
        label="Indhold"
        value={content}
        onChange={event => onContentChange(event.target.value)}
        fullWidth
        multiline
        minRows={3}
        sx={{ mt: 2 }}
        disabled={readOnly}
      />
      <Box sx={{ mt: 2, width: '100%' }}>
        <SingleSelect<JournalNoteContentTypeModel>
          label="Notattype"
          value={selectedContentType ?? undefined}
          onChange={option => onNoteTypeIdChange((option as JournalNoteContentTypeModel | null)?.id)}
          options={contentTypeOptions}
          getOptionLabel={option => option.name ?? ''}
          renderOption={(optionProps, option) => (
            <li {...(optionProps as React.HTMLAttributes<HTMLLIElement>)} key={option.id}>
              {option.name}
            </li>
          )}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          variant="outlined"
          disabled={readOnly}
        />
      </Box>
    </>
  );
}
